import {
  createContext,
  FC,
  PropsWithChildren,
  useContext,
  useMemo,
  useState,
} from 'react';
import { Helmet } from 'react-helmet-async';
import { useEffectOnce } from 'react-use';

export const pageTypes = [
  'multiProject',
  'projectOverview',
  /** Page for all items of a given resource */
  'resourceFilter',
  /** Page for a single item of a given resource */
  'resourceDetails',
  'assetDetails',
  'searchResults',
  'selectResults',
  'packageDetails',
  'databaseDetails',
  'sourceCollectionDetails',
  'other',
  'projectLineage',
  'performance',
  'recommendations',
] as const;

export type PageType = (typeof pageTypes)[number];

type PageContextData = {
  currentPage: PageType;
  setCurrentPage: (page: PageType) => void;
  lineageIngressPath?: string | null;
  setLineageIngressPath?: (path: string | null) => void;
};

const PageContext = createContext<PageContextData>({
  currentPage: 'other',
  setCurrentPage: () => {},
  lineageIngressPath: null,
  setLineageIngressPath: () => {},
});

export const useCurrentPage = () => {
  return useContext(PageContext);
};

export const PageContextProvider: FC<PropsWithChildren<{}>> = ({ children }) => {
  const [currentPage, setCurrentPage] = useState<PageType>('other');
  const [lineageIngressPath, setLineageIngressPath] = useState<string | null>(null);

  return (
    <PageContext.Provider
      value={useMemo(
        () => ({
          currentPage,
          setCurrentPage,
          lineageIngressPath,
          setLineageIngressPath,
        }),
        [currentPage, lineageIngressPath],
      )}
    >
      {children}
    </PageContext.Provider>
  );
};

interface PageParams {
  title: string;
  pageType: PageType;
}

export const Page: FC<PropsWithChildren<PageParams>> = ({
  title,
  pageType,
  children,
}) => {
  const { setCurrentPage } = useCurrentPage();

  useEffectOnce(() => {
    setCurrentPage(pageType);
  });

  const productName = 'Catalog';
  return (
    <>
      <Helmet>
        <meta
          name="description"
          content={`Explore your project with dbt ${productName}`}
        />
        <title>{`${title} | ${productName} | dbt Cloud`}</title>
      </Helmet>
      {children}
    </>
  );
};
